import { useRouter } from "@core/Api/useRouter";
import Path from "@core/FileProvider/Path/Path";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { useDiffStore } from "@core-ui/stores/DiffStore/DiffStore.provider";
import { useIsRevision } from "@ext/git/actions/Revisions/logic/hooks/useIsRevision";
import { setDiffEnabled } from "@ext/git/core/Diff/components/store/DiffViewModeStore";
import { disableLiveDiff, enableLiveDiff, useLiveDiffAttached } from "@ext/git/core/Diff/logic/live/liveDiffStore";
import { useCallback } from "react";

export const useDiffSwitch = () => {
	const router = useRouter();
	const isRevision = useIsRevision();
	const diffEnabled = useDiffStore((state) => !!state?.diff);
	const attached = useLiveDiffAttached();
	const catalogName = useCatalogPropsStore((state) => state.data?.name);
	const articlePath = useArticlePropsStore((state) => state.data?.ref?.path);
	const apiUrlCreator = ApiUrlCreatorService.value;

	const view = router.query?.mode === "markdown" ? "markdown" : "wysiwyg";
	const live = attached && !!catalogName && !!articlePath;

	const enable = useCallback(() => {
		if (live) {
			void enableLiveDiff({ catalogName, articlePath, oldScope: "HEAD", view, apiUrlCreator, router });
			return;
		}
		setDiffEnabled(true);
		router.pushQuery({
			...(router.query || {}),
			diff: "1",
			oldScope: !isRevision ? "HEAD" : undefined,
		});
	}, [live, view, catalogName, articlePath, router, isRevision]);

	/** Returns `true` when disabling goes the URL path and reloads the page itself. */
	const disable = useCallback(() => {
		if (live) {
			disableLiveDiff(router);
			return false;
		}
		setDiffEnabled(false);
		if (!router.query?.diff) return false;
		router.pushQuery({ ...(router.query || {}), diff: undefined, oldScope: undefined });
		return true;
	}, [live, router]);

	const toggle = useCallback(() => (diffEnabled ? disable() : enable()), [diffEnabled, enable, disable]);

	const isCurrentArticle = useCallback(
		(catalogRelativePath: string) => !!catalogName && Path.join(catalogName, catalogRelativePath) === articlePath,
		[catalogName, articlePath],
	);

	return { enable, disable, toggle, isOn: diffEnabled, isCurrentArticle };
};

export const useDiffToggle = () => useDiffSwitch().toggle;
